<!-- 表单控件多合一 -->
<template>
  <el-form-item
    class="dt-form-item"
    :key="`${inputType}-${refreshKey}`"
    v-bind="Object.assign({}, $attrs, itemBindProps)"
    :label="hasAinLabel ? '' : label"
    :prop="prop"
  >
    <component
      class="dt-form-item-input"
      :is="componentType"
      v-model="localValue"
      v-on="$listeners"
      v-bind="smartComponentAttr"
      :options="optionList"
      @error="(error) => (itemBindProps.error = error)"
      @correct="itemBindProps.error = ''"
      ref="component"
    >
      <!-- 传递插槽 -->
      <slot></slot>
      
      <template v-if="hasOptions">
        <component
          v-for="option in optionList"
          :key="option[propKey.key] || option[propKey.value]"
          :is="optionType"
          :label="option[propKey[inputType == 'select' ? 'label' : 'value']]"
          :value="option[propKey.value]"
          v-bind="{ ...option, ...(smartComponentAttr || {}).optionAttr }"
        >
          {{ option[propKey.label] }}
        </component>
      </template>
    </component>
  </el-form-item>
</template>

<script>
import { digStrFn, deepClone, strToFn, quickRequest } from "@/utils/form-input";

export default {
  name: "DtFormItem",
  components: {
    DtNumberRange: () => import("./components/DtNumberRange"),
    DtTimeRange: () => import("./components/DtTimeRange"),
    DtDateRange: () => import("./components/DtDateRange"),
  },
  model: {
    prop: "foreignValue",
  },
  props: {
    refreshKey: { default: new Date() * 1 },
    prop: {
      type: String,
      default: undefined,
    },
    // 控件类型
    inputType: {
      // 单选 radio
      // 单选（按钮型） radio-button
      // 多选 checkbox
      // 文本 input
      // 数字 input-number
      // 开关 switch
      // 下拉 select
      // 联级 cascader
      // 时间（任意） time-select
      // 时间（选项） time-picker
      // 日期 date-picker

      // 数值范围 dt-number-range
      // 时间范围（时分） dt-time-range
      // 日期范围（日月周） dt-date-range
      type: String,
      validator: (value) =>
        [
          "radio",
          "radio-button",
          "checkbox",
          "input",
          "input-number",
          "switch",
          "select",
          "cascader",
          "time-select",
          "time-picker",
          "date-picker",
          "dt-shop-cascader",
          "dt-number-range",
          "dt-multiple-input",
          "dt-time-range",
          "dt-date-range",
          "dt-json-input",
          "dt-object",
          "dt-array",
        ].includes(value),
      required: true,
    },
    // 标签
    label: {
      type: String,
    },
    // 给组件绑定的属性
    componentAttr: {
      type: Object,
      default: () => ({}),
    },
    // 使用逗号分隔代替数组结构
    valueParseString: {
      type: Boolean,
      default: true,
    },
    // 选项列表||一个返回选项列表的函数（字符串）
    options: {
      type: [Array, Function, String],
      default: () => [],
    },
    // key映射
    propKey: {
      type: Object,
      default: () => ({
        label: "label",
        value: "value",
        key: "key",
      }),
    },
    // 按钮类型多选
    isCheckboxBtn: {
      type: Boolean,
      default: false,
    },
    // v-model绑定值
    foreignValue: {},
    /**
     * el-form-item attr/props
     */
    formItemProp: {
      type: [String, undefined],
      default: undefined,
    },
    // 单值控件生成多个值
    valueRepeat: {
      type: Number,
      default: 0,
    },
    // 是否尝试解析函数字符串（传递给组件的属性）
    parseFnStr: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localValue: undefined,
      optionList: [],
      itemBindProps: { error: "" },
    };
  },
  computed: {
    // 控件类型
    componentType() {
      let componentType = "";
      if (/^dt-/.test(this.inputType)) componentType = this.inputType;
      else {
        componentType = `el-${this.inputType}`;
        if (["radio", "radio-button", "checkbox"].includes(this.inputType) && Array.isArray(this.options) && this.options.length)
          componentType = `el-${this.inputType.match(/^(.*?)(-|$)/)[1]}-group`;
      }
      return componentType;
    },
    // 是否具有label
    hasAinLabel() {
      return ["dt-shop-cascader"].includes(this.inputType);
    },
    // 是否具有options
    hasOptions() {
      // 级联选择器用options而不是内嵌，奇葩😓
      return ["radio", "radio-button", "checkbox", "select"].includes(this.inputType);
    },
    // 子选项类型
    optionType() {
      if (this.inputType === "select") return "el-option";
      let optionType = `el-${this.inputType}`;
      if (this.isCheckboxBtn) optionType += "-button";
      return optionType;
    },
    // 值类型
    valueType() {
      if (
        [
          "radio",
          "radio-button",
          "input",
          "input-number",
          "switch",
          "time-select",
          "dt-shop-cascader",
          "dt-multiple-input",
          "dt-json-input",
          "dt-object",
          "dt-array",
        ].includes(this.inputType)
      )
        return "single";
      if (this.inputType === "checkbox" && Array.isArray(this.options) && this.options.length === 1) return "single";
      if (["select", "cascader"].includes(this.inputType) && !this.$attrs.multiple) return "single";
      if (["dt-number-range", "dt-time-range", "dt-date-range"].includes(this.inputType)) return "multiple";
      if ("time-picker" === this.inputType) {
        if (this.componentAttr.isRange || this.componentAttr["is-range"]) return "multiple";
        else return "single";
      }
      if ("date-picker" === this.inputType) {
        if (this.componentAttr.type.includes("range")) return "multiple";
        else return "single";
      }
      if ((["select", "cascader"].includes(this.inputType) && this.$attrs.multiple) || this.inputType === "checkbox") return "array";
      return console.error("找不到值类型匹配值！");
    },
    // 绑定给组件的值（转化字符串为函数处理）
    smartComponentAttr() {
      if (!this.componentAttr) return {};
      const result = deepClone(this.componentAttr);
      if (this.parseFnStr) digStrFn(result);
      return result;
    },
    componentFormClass() {
      if (this.label) return "";
      else return "cmp-form-no-mb";
    },
  },
  watch: {
    localValue() {
      this.updateValue();
    },
    foreignValue: {
      deep: true,
      handler() {
        this.syncValue();
      },
    },
    options: {
      deep: true,
      handler() {
        this._initOptions();
      },
    },
  },
  methods: {
    syncValue() {
      let value = this.valueRepeat ? this.foreignValue[0] : this.foreignValue;
      // 转化布尔值给开关
      if (this.inputType === "switch") return (this.localValue = !!value);
      // v-model解析
      if (value === undefined) return;
      if (this.valueType === "array" && this.valueParseString) {
        // 数组转字符串
        if (value == "") this.localValue = [];
        else this.localValue = value.split(",");
      } else this.localValue = value;
    },
    updateValue() {
      let value = this.localValue;
      // 数组类型值
      if (this.valueParseString && this.valueType === "array") value = value.join(",");
      // 输出重复值
      if (this.valueRepeat) value = new Array(this.valueRepeat).fill(value);
      this.$emit("input", value);
      // 多个值时，一一返回update事件更新sync值
      if (this.valueType === "multiple") {
        (value || []).forEach((singleVal, index) => {
          this.$emit(`update:sync${index}`, singleVal);
        });
      }
      return value;                                                                                                                    
    },
    async _initOptions() {
      const BASE_URL = process.env.VUE_APP_API_ROOT
      // 选项
      if (Array.isArray(this.options)) this.optionList = this.options;
      else if (typeof this.options === "function") this.optionList = await this.options({ quickRequest, domainEnum: BASE_URL });
      else if (typeof this.options === "string" && strToFn(this.options)) {
        const optionsGeneratorFn = strToFn(this.options),
          requestPromise = optionsGeneratorFn({ quickRequest, domainEnum: BASE_URL });
        this.optionList = await requestPromise;
      } else this.optionList = [];
    },
  },
  created() {
    this._initOptions();
    this.syncValue();
    // 控件变更时，需要更新一次值进行校准
    this.updateValue();
  },
  destroyed() {
    this.$emit("destroyed");
  },
};
</script>

<style scoped>
.cmp-form-no-mb {
  margin-bottom: 0;
}
/* 解决label换行时多余换行的问题 */
.dt-form-item-input::v-deep > .el-form-item__content > br {
  display: none;
}
</style>
